// useAuth.ts — local auth hook for the authz SPA.
//
// The session JWT is cached in localStorage ('identity_token'). authz issues
// 24h tokens, so to keep users signed in we run a sliding refresh: a single
// module-level timer re-mints the token via POST /api/refresh shortly before it
// expires (which also rolls the identity_auth cookie), and on app load a
// near-expiry token is refreshed immediately so /api/me doesn't 401. As long as
// the user returns within the token's lifetime they never have to log in again;
// a token that has fully expired still falls through to /login.
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

export interface AuthState {
    email: string
    roles: string[]
    scopes: string[]
    photoUrl: string
    loading: boolean
    error: string
}

const TOKEN_KEY = 'identity_token'
// Refresh this far before the token's exp so an in-flight request never races
// expiry, and so a tab left open overnight renews instead of logging out.
const REFRESH_SKEW_MS = 5 * 60 * 1000

// Module-level so every useAuth consumer shares one timer / one in-flight
// refresh rather than each mounting its own.
let refreshTimer: ReturnType<typeof setTimeout> | null = null
let refreshInFlight: Promise<boolean> | null = null

// decodeExp returns the JWT's `exp` (unix seconds), or null if unparseable.
function decodeExp(token: string): number | null {
    try {
        const part = token.split('.')[1]
        const b64 = part.replace(/-/g, '+').replace(/_/g, '/')
        const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
        const payload = JSON.parse(atob(padded))
        return typeof payload.exp === 'number' ? payload.exp : null
    } catch {
        return null
    }
}

// scheduleRefresh (re)arms the single module timer to fire REFRESH_SKEW_MS
// before the given token expires.
function scheduleRefresh(token: string): void {
    if (refreshTimer) clearTimeout(refreshTimer)
    const exp = decodeExp(token)
    if (!exp) return
    const delay = Math.max(0, exp * 1000 - Date.now() - REFRESH_SKEW_MS)
    refreshTimer = setTimeout(() => { void refreshToken() }, delay)
}

// refreshToken swaps the cached token for a freshly minted one. Returns false
// (without clearing the cached token) if there's nothing to refresh or the
// server rejects it; the next /api/me call then drives the redirect to /login.
export async function refreshToken(): Promise<boolean> {
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) return false
    if (refreshInFlight) return refreshInFlight
    refreshInFlight = (async () => {
        try {
            const res = await fetch('/api/refresh', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                credentials: 'include',
            })
            if (!res.ok) return false
            const data = await res.json()
            if (!data.token) return false
            localStorage.setItem(TOKEN_KEY, data.token)
            scheduleRefresh(data.token)
            return true
        } catch {
            return false
        } finally {
            refreshInFlight = null
        }
    })()
    return refreshInFlight
}

export function useAuth(): AuthState {
    const navigate = useNavigate()
    const [state, setState] = useState<AuthState>({
        email: '', roles: [], scopes: [], photoUrl: '', loading: true, error: '',
    })

    const fetchMe = useCallback(async () => {
        try {
            const token = localStorage.getItem(TOKEN_KEY)
            const res = await fetch('/api/me', {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                credentials: 'include',
            })
            if (res.status === 401) {
                navigate('/login')
                return
            }
            if (!res.ok) throw new Error('Failed to load profile')
            const data = await res.json()
            setState({
                email: data.email,
                roles: data.roles || [],
                scopes: data.scopes || [],
                photoUrl: data.photo_url || '',
                loading: false,
                error: '',
            })
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Failed to load profile'
            setState(prev => ({ ...prev, loading: false, error: msg }))
        }
    }, [navigate])

    useEffect(() => {
        let cancelled = false
        const start = async () => {
            const token = localStorage.getItem(TOKEN_KEY)
            if (token) {
                const exp = decodeExp(token)
                // Near or past expiry → roll it now so /api/me succeeds; else
                // just make sure the proactive timer is armed.
                if (exp && exp * 1000 - Date.now() < REFRESH_SKEW_MS) {
                    await refreshToken()
                } else {
                    scheduleRefresh(token)
                }
            }
            if (!cancelled) await fetchMe()
        }
        void start()
        return () => { cancelled = true }
    }, [fetchMe])

    return state
}

export async function apiFetch(path: string, options?: RequestInit): Promise<Response> {
    const token = localStorage.getItem(TOKEN_KEY)
    const headers: Record<string, string> = {
        ...(options?.headers as Record<string, string> || {}),
    }
    if (token) headers['Authorization'] = `Bearer ${token}`

    return fetch(path, { ...options, headers, credentials: 'include' })
}
